import React, { useCallback, useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import PropTypes from "prop-types"
import { Box, Button, Typography } from "@mui/material"
import { Rows } from "../../../manager/carto"
import { useEveListen } from "react-eve-hook"
import * as L from "leaflet"
import _ from "ansuko"

const MemoPopupContent = ({ memo, onUpdate, onDelete, onClose }) => (
  <Box sx={{ minWidth: 220, maxWidth: 280 }}>
    <Typography
      variant="body2"
      sx={{
        mb: 2,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        color: "text.primary",
      }}
    >
      {memo || ""}
    </Typography>
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 1,
        pt: 2,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      {onDelete && (
        <Button
          size="small"
          color="error"
          variant="outlined"
          onClick={onDelete}
        >
          削除
        </Button>
      )}
      <Button
        size="small"
        variant="outlined"
        onClick={() => {
          onClose()
          onUpdate()
        }}
      >
        更新
      </Button>
    </Box>
  </Box>
)

MemoPopupContent.propTypes = {
  memo: PropTypes.string,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
}

const MapMemoLayer = ({ map, isMemoMode, onMemoClick, onDeleteMemoClick }) => {
  const [rows, setRows] = useState([])
  const markersRef = useRef([])
  const popupContainersRef = useRef([])

  const loadData = useCallback(() => {
    Rows(`
        SELECT
            cartodb_id,
            ST_Y(the_geom) AS latitude,
            ST_X(the_geom) AS longitude,
            memo
        FROM tokyo_roadsidetrees_memo
        `).then(setRows)
  }, [])

  useEffect(() => {
    if (!map || _.isEmpty(rows)) {
      return undefined
    }

    const memoIcon = L.AwesomeMarkers?.icon
      ? L.AwesomeMarkers.icon({
          prefix: "fa",
          icon: "comment",
          markerColor: "blue",
        })
      : undefined
    const containers = []
    markersRef.current = rows.map((row) => {
      const m = L.marker(
        [row.latitude, row.longitude],
        memoIcon ? { icon: memoIcon } : {}
      )
      if (row.memo) {
        const container = document.createElement("div")
        containers.push(container)
        ReactDOM.render(
          <MemoPopupContent
            memo={row.memo}
            onUpdate={() => onMemoClick(row)}
            onDelete={
              row.cartodb_id && onDeleteMemoClick
                ? () => {
                    m.closePopup()
                    onDeleteMemoClick(row)
                  }
                : undefined
            }
            onClose={() => m.closePopup()}
          />,
          container
        )
        m.bindPopup(container, { maxWidth: 300 })
      }
      if (isMemoMode) {
        m.addTo(map)
      }
      return m
    })
    popupContainersRef.current = containers
    return () => {
      markersRef.current.forEach((m) => m.remove())
      popupContainersRef.current.forEach((container) => {
        if (container) ReactDOM.unmountComponentAtNode(container)
      })
      popupContainersRef.current = []
      markersRef.current = []
    }
  }, [map, rows, isMemoMode, onMemoClick, onDeleteMemoClick])

  useEffect(() => {
    if (map) {
      loadData()
    }
  }, [map, loadData])

  useEveListen("EVENT_MEMO_UPDATED", () => loadData())

  return null
}

MapMemoLayer.propTypes = {
  map: PropTypes.any,
  isMemoMode: PropTypes.bool,
  onMemoClick: PropTypes.func,
  onDeleteMemoClick: PropTypes.func,
}

export default MapMemoLayer
